import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Card,
  Combobox,
  LineChart,
  NoData,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import {
  EMBEDDED_EARNING_TIME_PERIODS,
  EmbeddedEarningTimePeriodValue,
  getEmbeddedEarningTimePeriodOptions,
} from 'pages/Rewards/constants/embeddedEarningTimePeriods'
import { useChartProps } from 'pages/Rewards/hooks/useChartProps'
import { useMonthlyCustomersEarnings } from 'pages/Rewards/hooks/useMontlyCustomersEarnings'

interface RewardsEmbeddedEarningsChartProps {
  currency: string
}

export const RewardsEmbeddedEarningsChart = ({ currency }: RewardsEmbeddedEarningsChartProps) => {
  const { t } = useTranslation()
  const [timePeriod, setTimePeriod] = useState<EmbeddedEarningTimePeriodValue>(
    EMBEDDED_EARNING_TIME_PERIODS.SIX_MONTHS
  )

  const { rewardsData, isLoading } = useMonthlyCustomersEarnings(timePeriod, currency)

  const chartProps = useChartProps(rewardsData, 'step')

  const handleTimePeriodChange = (value: string) => {
    setTimePeriod(value as EmbeddedEarningTimePeriodValue)
  }

  if (!rewardsData?.length) {
    return <NoData dataName="customer earnings" />
  }

  return (
    <Card variant="static" full>
      <Box flexCol gapY={24}>
        <Box flexCol gap={16} className="xl:flex-row xl:justify-between xl:items-center">
          <h4 className="text-h4 text-foreground-prominent">
            {t('rewards.embeddedCustomers.totalEarnings')}
          </h4>

          <Box className="min-w-[200px]">
            <Combobox
              items={getEmbeddedEarningTimePeriodOptions(t)}
              id="earnings-chart-period"
              label={t('rewards.embeddedCustomers.earningsChartPeriod')}
              defaultValue={EMBEDDED_EARNING_TIME_PERIODS.SIX_MONTHS}
              onChange={handleTimePeriodChange}
            />
          </Box>
        </Box>

        <Box className="mt-2">
          {isLoading ? <RowsSkeletonLoader rows={4} /> : <LineChart {...chartProps} />}
        </Box>
      </Box>
    </Card>
  )
}
